import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The device's position, with the caveats a field app cannot ignore.
 *
 * GPS lies more than people expect. Accuracy is routinely 20-50m inside a
 * shopping centre or between big-box buildings, and iOS will happily hand back
 * a **cached fix from somewhere else entirely** the instant you ask. A site
 * captured from a stale fix is worse than no site: it looks precise and sends
 * someone to the wrong parcel.
 *
 * So this hook does three things beyond calling the browser API:
 *
 *  - asks for high accuracy, and waits rather than accepting the quick answer
 *  - **rejects fixes older than `maxAgeMs`**, using `position.timestamp`
 *  - surfaces `accuracy` so the UI can draw the uncertainty rather than hide it
 *
 * Requires a secure context. Over plain HTTP this fails silently on iOS — no
 * prompt, no callback — which is why device testing goes through the tunnel.
 * See CLAUDE.md → Testing on a Real Device.
 */

export type PositionStatus =
	| 'idle'
	| 'locating'
	| 'ready'
	| 'denied'
	| 'unavailable'
	| 'timeout'
	| 'insecure'
	| 'stale'

export interface DevicePosition {
	latitude: number
	longitude: number
	/** Radius in metres the device believes it is within. */
	accuracy: number
	timestamp: number
}

export interface UseCurrentPositionOptions {
	/** Start locating on mount. Off by default — asking for GPS should be a choice. */
	immediate?: boolean
	/** Reject a fix older than this. Defaults to 30s. */
	maxAgeMs?: number
	/** Keep updating as the user moves. */
	watch?: boolean
}

export interface UseCurrentPositionResult {
	position: DevicePosition | null
	status: PositionStatus
	/** Set when status is denied, unavailable or timeout. */
	error: string | null
	request: () => void
}

const MESSAGES: Record<Exclude<PositionStatus, 'idle' | 'locating' | 'ready'>, string> = {
	denied:
		'Location access is off for this site. iOS will not ask again, so it has to be re-enabled in Settings.',
	unavailable: 'This device cannot report a location right now.',
	timeout: 'Could not get a location fix. Moving into the open usually helps.',
	stale:
		'Your device only offered an old location fix, so it was discarded rather than shown as where you are now. Moving into the open and trying again usually helps.',
	insecure:
		'Location needs a secure connection. Open the app over HTTPS rather than a plain address.',
}

const useCurrentPosition = ({
	immediate = false,
	maxAgeMs = 30_000,
	watch = false,
}: UseCurrentPositionOptions = {}): UseCurrentPositionResult => {
	const [position, setPosition] = useState<DevicePosition | null>(null)
	const [status, setStatus] = useState<PositionStatus>('idle')
	const watchIdRef = useRef<number | null>(null)

	const accept = useCallback(
		(fix: GeolocationPosition) => {
			// iOS can return a cached fix instantly. A stale one looks precise and
			// points at wherever the phone last had signal, so it is discarded
			// rather than shown.
			//
			// Discarding it SILENTLY was a dead end: `getCurrentPosition` fires
			// once, so nothing ever replaced the rejected fix and the screen sat on
			// "Finding your location…" for ever, with no explanation and no retry.
			// Saying so is the difference between a slow app and a broken one.
			if (Date.now() - fix.timestamp > maxAgeMs) {
				setStatus('stale')
				return
			}

			setPosition({
				latitude: fix.coords.latitude,
				longitude: fix.coords.longitude,
				accuracy: fix.coords.accuracy,
				timestamp: fix.timestamp,
			})
			setStatus('ready')
		},
		[maxAgeMs],
	)

	const fail = useCallback((error: GeolocationPositionError) => {
		if (error.code === error.PERMISSION_DENIED) setStatus('denied')
		else if (error.code === error.TIMEOUT) setStatus('timeout')
		else setStatus('unavailable')
	}, [])

	const request = useCallback(() => {
		if (typeof navigator === 'undefined' || !navigator.geolocation) {
			setStatus('unavailable')
			return
		}
		// A secure context is required. Saying so beats a silent failure that
		// looks like a broken app.
		if (typeof window !== 'undefined' && window.isSecureContext === false) {
			setStatus('insecure')
			return
		}

		setStatus('locating')

		const options: PositionOptions = {
			enableHighAccuracy: true,
			// 0 so the browser does not hand back its own cached fix; staleness is
			// judged here, against the fix's own timestamp.
			maximumAge: 0,
			timeout: 15_000,
		}

		if (watch) {
			watchIdRef.current = navigator.geolocation.watchPosition(accept, fail, options)
		} else {
			navigator.geolocation.getCurrentPosition(accept, fail, options)
		}
	}, [accept, fail, watch])

	useEffect(() => {
		if (immediate) request()
		return () => {
			if (watchIdRef.current !== null && navigator.geolocation) {
				navigator.geolocation.clearWatch(watchIdRef.current)
				watchIdRef.current = null
			}
		}
	}, [immediate, request])

	return {
		position,
		status,
		error: status in MESSAGES ? MESSAGES[status as keyof typeof MESSAGES] : null,
		request,
	}
}

export default useCurrentPosition
